"use client";

import { useEffect } from "react";
import Link from "next/link";

// Catches any uncaught render error. Without this the customer lands on Next's raw default page,
// which in production is an unbranded, near-blank screen with no way forward.
export default function ErrorBoundary({
  error,
  reset
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Surface it for whoever is debugging; the customer never sees the raw message.
    console.error("Unhandled portal error:", error);
  }, [error]);

  return (
    <main className="flex min-h-screen items-center justify-center bg-gray-50 px-5">
      <div className="w-full max-w-md rounded-2xl border border-gray-200/70 bg-white p-8 text-center shadow-card">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-danger-50 text-danger-600 dark:bg-danger-500/15 dark:text-danger-400">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} className="h-6 w-6">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
            />
          </svg>
        </span>

        <h1 className="mt-4 text-lg font-bold tracking-tight text-gray-900">Something went wrong</h1>
        <p className="mt-1.5 text-sm text-gray-600">
          This page hit an unexpected error. Nothing you sent was lost — try again, and contact support if it keeps happening.
        </p>

        {/* The digest is the only safe identifier to show: it lets support find the server-side log
            without exposing a stack trace or internal message to the customer. */}
        {error.digest && (
          <p className="mt-3 font-mono text-xs text-gray-400">Reference: {error.digest}</p>
        )}

        <div className="mt-6 flex justify-center gap-3">
          <button
            onClick={reset}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-pure-white shadow-sm transition hover:bg-brand-700"
          >
            Try again
          </button>
          <Link
            href="/dashboard"
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50"
          >
            Back to dashboard
          </Link>
        </div>
      </div>
    </main>
  );
}
